import React, { useState, useRef } from "react";
import axios from "axios";
import Webcam from "react-webcam";

export default function UploadInvoice() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ocrEngine, setOcrEngine] = useState("mistral");
  const [editing, setEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraMode, setCameraMode] = useState("environment"); // 'environment' pour l'arrière, 'user' pour l'avant
  const webcamRef = useRef(null);

  const videoConstraints = {
    facingMode: cameraMode,
    width: { ideal: 1280 },
    height: { ideal: 720 }
  };

  const capturePhoto = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        // Convertir la base64 en File
        const blob = dataURLtoBlob(imageSrc);
        const fileName = `photo_facture_${Date.now()}.jpg`;
        const capturedFile = new File([blob], fileName, { type: "image/jpeg" });
        setFile(capturedFile);
        setShowCamera(false);
        
        // Afficher un message de succès
        alert("📸 Photo capturée avec succès ! Prêt à analyser.");
      }
    }
  };

  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const switchCamera = () => {
    setCameraMode(prevMode => prevMode === "environment" ? "user" : "environment");
  };

  const handleFileUpload = (uploadedFile) => {
    setFile(uploadedFile);
    setResult(null);
    setError(null);
    setEditing(false);
  };

  const submit = async () => {
    if (!file) {
      alert("Veuillez choisir un fichier ou prendre une photo avant de continuer.");
      return;
    }

    setLoading(true);
    setError(null);
    setEditing(false);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("ocrEngine", ocrEngine);

      console.log(`Envoi du fichier avec ${ocrEngine} OCR:`, file.name);

      const response = await axios.post(`/api/invoices/upload`, formData, {
        headers: { 
          "Content-Type": "multipart/form-data",
        },
        timeout: ocrEngine === "mistral" ? 60000 : 30000,
      });

      console.log("Réponse reçue:", response.data);

      if (response.data && response.data.invoice) {
        setResult(response.data.invoice);
        setEditedData(response.data.invoice);
        alert(`✅ Facture analysée avec ${ocrEngine === "mistral" ? "OCR HD" : "OCR standard"} !`);
      } else {
        alert("⚠️ Aucun résultat trouvé dans la réponse !");
      }
    } catch (error) {
      console.error("Erreur upload :", error);
      setError(error.message);
      
      if (error.response) {
        alert(`Erreur serveur (${error.response.status}) : ${error.response.data.message || "Erreur inconnue."}`);
      } else if (error.request) {
        alert("⚠️ Aucun contact avec le serveur. Vérifiez que le backend tourne bien.");
      } else {
        alert("❌ Erreur : " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleSaveEdit = () => {
    setEditing(false);
    setResult({ ...result, ...editedData });
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditedData(result);
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const confirmSave = async () => {
    if (!result) return;

    setSaving(true);
    try {
      const response = await axios.post(`/api/invoices/${result._id}/confirm`, editedData);
      
      if (response.data && response.data.success) {
        alert("✅ Facture enregistrée avec succès !");
        setResult(response.data.invoice || result);
      } else {
        alert("⚠️ Erreur lors de l'enregistrement !");
      }
    } catch (error) {
      console.error("Erreur confirmation :", error);
      alert("❌ Erreur lors de l'enregistrement : " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const generateEnhancedPDF = async () => {
    if (!result) return;

    try {
      const response = await axios.get(`/api/invoices/${result._id}/enhanced-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_amelioree_${result.invoiceNumber || 'sans-numero'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert("📄 PDF amélioré généré avec succès !");
    } catch (error) {
      console.error("Erreur génération PDF :", error);
      alert("❌ Erreur lors de la génération du PDF : " + error.message);
    }
  };

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto", padding: "2rem", fontFamily: "Arial, sans-serif" }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <h1 style={{ color: "#1a202c", fontSize: "1.875rem", fontWeight: "700", margin: "0 0 0.5rem 0" }}>
          📤 Uploader une facture
        </h1>
        <p style={{ color: "#718096", fontSize: "1rem", margin: "0" }}>
          extraction 
        </p>
      </div>

      {/* Modal de caméra */}
      {showCamera && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.9)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "20px"
        }}>
          <div style={{
            width: "100%",
            maxWidth: "600px",
            position: "relative"
          }}>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              style={{
                width: "100%",
                borderRadius: "12px",
                border: "2px solid white"
              }}
            />
            
            {/* Boutons de contrôle caméra */}
            <div style={{
              position: "absolute",
              bottom: "20px",
              left: "0",
              right: "0",
              display: "flex",
              justifyContent: "center",
              gap: "20px",
              padding: "20px"
            }}>
              <button
                onClick={capturePhoto}
                style={{
                  width: "70px",
                  height: "70px",
                  borderRadius: "50%",
                  border: "4px solid white",
                  background: "rgba(255, 255, 255, 0.2)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px"
                }}
              >
                📸
              </button>
              
              <button
                onClick={switchCamera}
                style={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  border: "2px solid white",
                  background: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px"
                }}
              >
                🔄
              </button>
              
              <button
                onClick={() => setShowCamera(false)}
                style={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "50%",
                  border: "2px solid white",
                  background: "rgba(255, 0, 0, 0.2)",
                  color: "white",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px"
                }}
              >
                ✕
              </button>
            </div>
            
            <div style={{
              color: "white",
              textAlign: "center",
              marginTop: "20px",
              fontSize: "0.9rem"
            }}>
              {cameraMode === "environment" ? "📷 Caméra arrière" : "🤳 Caméra avant"} • 
              Centrez bien la facture dans le cadre pour une meilleure capture
            </div>
          </div>
        </div>
      )}

      {/* Section Upload */}
      <div style={{ background: "white", padding: "2rem", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0" }}>
        
        {/* Sélecteur OCR */}
        <div style={{ marginBottom: "1.5rem", padding: "1rem", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
          <label style={{ display: "block", marginBottom: "0.75rem", fontWeight: "600", color: "#2d3748" }}>
            Moteur OCR :
          </label>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.75rem 1rem", border: `2px solid ${ocrEngine === "mistral" ? "#4299e1" : "#e2e8f0"}`, borderRadius: "8px", cursor: "pointer", transition: "all 0.2s ease", flex: "1", minWidth: "200px", background: ocrEngine === "mistral" ? "#ebf8ff" : "white" }}>
              <input
                type="radio"
                value="mistral"
                checked={ocrEngine === "mistral"}
                onChange={(e) => setOcrEngine(e.target.value)}
                style={{ display: "none" }}
              />
              <span style={{ flex: "1", fontWeight: ocrEngine === "mistral" ? "600" : "500", color: ocrEngine === "mistral" ? "#4299e1" : "#4a5568" }}>
                HQ Extract
              </span>
              <span style={{ padding: "0.25rem 0.5rem", background: ocrEngine === "mistral" ? "#4299e1" : "#e2e8f0", borderRadius: "4px", fontSize: "0.75rem", fontWeight: "600", color: "white" }}>
                Haute précision
              </span>
            </label>
            
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.75rem 1rem", border: `2px solid ${ocrEngine === "standard" ? "#4299e1" : "#e2e8f0"}`, borderRadius: "8px", cursor: "pointer", transition: "all 0.2s ease", flex: "1", minWidth: "200px", background: ocrEngine === "standard" ? "#ebf8ff" : "white" }}>
              <input
                type="radio"
                value="standard"
                checked={ocrEngine === "standard"}
                onChange={(e) => setOcrEngine(e.target.value)}
                style={{ display: "none" }}
              />
              <span style={{ flex: "1", fontWeight: ocrEngine === "standard" ? "600" : "500", color: ocrEngine === "standard" ? "#4299e1" : "#4a5568" }}>
                OCR Standard
              </span>
              <span style={{ padding: "0.25rem 0.5rem", background: ocrEngine === "standard" ? "#4299e1" : "#e2e8f0", borderRadius: "4px", fontSize: "0.75rem", fontWeight: "600", color: "white" }}>
                Rapide
              </span>
            </label>
          </div>
        </div>

        {/* Info Mistral */}
        {ocrEngine === "mistral" && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", padding: "1rem", background: "#f0fff4", border: "1px solid #9ae6b4", borderRadius: "8px", color: "#22543d", marginBottom: "1.5rem" }}>
            <div style={{ fontSize: "1.25rem" }}>ℹ️</div>
            <div style={{ flex: "1", fontSize: "0.875rem" }}>
              <strong>Extraction</strong> extraction plus précise. 
              Temps d'analyse : 10-30 secondes.
            </div>
          </div>
        )}

        {/* Sélection de fichier et caméra */}
        <div style={{ marginBottom: "1.5rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
            {/* Bouton Caméra */}
            <button
              onClick={() => setShowCamera(true)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.75rem",
                padding: "1rem",
                border: "2px dashed #4299e1",
                borderRadius: "8px",
                background: "#ebf8ff",
                color: "#4299e1",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s ease",
                fontSize: "1rem",
                width: "100%"
              }}
              onMouseOver={(e) => e.currentTarget.style.background = "#bee3f8"}
              onMouseOut={(e) => e.currentTarget.style.background = "#ebf8ff"}
            >
              <span style={{ fontSize: "1.5rem" }}>📸</span>
              <div>Prendre une photo</div>
            </button>

            {/* Sélecteur de fichier */}
            <label style={{ cursor: "pointer" }}>
              <input
                type="file"
                accept="image/*,.pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileUpload(e.target.files[0])}
                style={{ display: "none" }}
              />
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.75rem",
                padding: "1rem",
                border: "2px dashed #cbd5e0",
                borderRadius: "8px",
                background: "#f7fafc",
                color: "#4a5568",
                fontWeight: "600",
                transition: "all 0.2s ease",
                width: "100%"
              }}>
                <span style={{ fontSize: "1.5rem" }}>📁</span>
                <div>Choisir un fichier</div>
              </div>
            </label>
          </div>

          {/* Aperçu du fichier sélectionné */}
          {file && (
            <div style={{
              padding: "1rem",
              background: "#f0fff4",
              border: "1px solid #9ae6b4",
              borderRadius: "8px",
              marginTop: "1rem"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <span style={{ fontSize: "1.5rem" }}>
                  {file.type.includes("image") ? "🖼️" : "📄"}
                </span>
                <div style={{ flex: "1" }}>
                  <div style={{ fontWeight: "600", color: "#22543d", marginBottom: "0.25rem" }}>
                    Fichier sélectionné :
                  </div>
                  <div style={{ color: "#38a169", fontSize: "0.95rem", wordBreak: "break-all" }}>
                    {file.name} ({Math.round(file.size / 1024)} KB)
                  </div>
                </div>
                <button
                  onClick={() => setFile(null)}
                  style={{
                    padding: "0.25rem 0.5rem",
                    border: "1px solid #f56565",
                    borderRadius: "4px",
                    background: "#fed7d7",
                    color: "#c53030",
                    fontSize: "0.875rem",
                    cursor: "pointer"
                  }}
                >
                  Supprimer
                </button>
              </div>
              
              {/* Aperçu de l'image si c'est une image */}
              {file.type.includes("image") && (
                <div style={{ marginTop: "1rem", textAlign: "center" }}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt="Aperçu"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0"
                    }}
                  />
                  <div style={{ fontSize: "0.875rem", color: "#718096", marginTop: "0.5rem" }}>
                    Aperçu de la facture
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bouton submit */}
        <button 
          onClick={submit} 
          disabled={loading || !file}
          style={{ 
            width: "100%", 
            background: (!file || loading) ? "#90cdf4" : "#4299e1", 
            color: "white", 
            border: "none", 
            padding: "0.875rem 1.5rem", 
            borderRadius: "8px", 
            fontSize: "1rem", 
            fontWeight: "600", 
            cursor: (!file || loading) ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
            opacity: (!file || loading) ? 0.6 : 1
          }}
        >
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
              <div style={{ width: "16px", height: "16px", border: "2px solid transparent", borderTop: "2px solid white", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
              {ocrEngine === "mistral" ? "Analyse IA en cours..." : "Envoi en cours..."}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
              <span>⚡</span>
              {ocrEngine === "mistral" ? "Analyser " : "Uploader & OCR"}
            </div>
          )}
        </button>

        {/* Erreur */}
        {error && (
          <div style={{ marginTop: "1rem", padding: "1rem", background: "#fed7d7", border: "1px solid #feb2b2", borderRadius: "8px", color: "#c53030" }}>
            <strong>Erreur :</strong> {error}
          </div>
        )}
      </div>

      {/* Résultats avec édition */}
      {result && (
        <div style={{ marginTop: "2rem", background: "white", padding: "2rem", borderRadius: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0" }}>
          <h3 style={{ color: "#1a202c", fontSize: "1.25rem", fontWeight: "600", margin: "0 0 1.5rem 0", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {editing ? "✏️ Modification de la facture" : "✅ Facture importée avec succès"}
            {result.ocrEngine === "mistral" && (
              <span style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white", padding: "0.25rem 0.75rem", borderRadius: "12px", fontSize: "0.75rem", fontWeight: "600" }}>
                Powered by AI
              </span>
            )}
          </h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
            {/* Fournisseur */}
            <div>
              <label style={{ fontSize: "0.875rem", color: "#718096", fontWeight: "500" }}>Fournisseur</label>
              {editing ? (
                <input
                  type="text"
                  value={editedData.supplierName || ""}
                  onChange={(e) => handleInputChange("supplierName", e.target.value)}
                  style={{ width: "100%", padding: "0.5rem", border: "1px solid #cbd5e0", borderRadius: "4px", fontSize: "1rem" }}
                />
              ) : (
                <div style={{ fontSize: "1rem", color: "#1a202c", fontWeight: "600", padding: "0.5rem 0" }}>
                  {result.supplierName || "-"}
                </div>
              )}
            </div>

            {/* Numéro de facture */}
            <div>
              <label style={{ fontSize: "0.875rem", color: "#718096", fontWeight: "500" }}>N° facture</label>
              {editing ? (
                <input
                  type="text"
                  value={editedData.invoiceNumber || ""}
                  onChange={(e) => handleInputChange("invoiceNumber", e.target.value)}
                  style={{ width: "100%", padding: "0.5rem", border: "1px solid #cbd5e0", borderRadius: "4px", fontSize: "1rem" }}
                />
              ) : (
                <div style={{ fontSize: "1rem", color: "#1a202c", fontWeight: "600", padding: "0.5rem 0" }}>
                  {result.invoiceNumber || "-"}
                </div>
              )}
            </div>

            {/* Date */}
            <div>
              <label style={{ fontSize: "0.875rem", color: "#718096", fontWeight: "500" }}>Date</label>
              {editing ? (
                <input
                  type="date"
                  value={editedData.date ? new Date(editedData.date).toISOString().split('T')[0] : ""}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                  style={{ width: "100%", padding: "0.5rem", border: "1px solid #cbd5e0", borderRadius: "4px", fontSize: "1rem" }}
                />
              ) : (
                <div style={{ fontSize: "1rem", color: "#1a202c", fontWeight: "600", padding: "0.5rem 0" }}>
                  {result.date ? new Date(result.date).toLocaleDateString() : "-"}
                </div>
              )}
            </div>

            {/* Montant HT */}
            <div>
              <label style={{ fontSize: "0.875rem", color: "#718096", fontWeight: "500" }}>Montant HT</label>
              {editing ? (
                <input
                  type="number"
                  step="0.01"
                  value={editedData.subtotalHT || ""}
                  onChange={(e) => handleInputChange("subtotalHT", parseFloat(e.target.value))}
                  style={{ width: "100%", padding: "0.5rem", border: "1px solid #cbd5e0", borderRadius: "4px", fontSize: "1rem" }}
                />
              ) : (
                <div style={{ fontSize: "1rem", color: "#1a202c", fontWeight: "600", padding: "0.5rem 0" }}>
                  {result.subtotalHT ? `${result.subtotalHT} ${result.currency || 'TND'}` : "-"}
                </div>
              )}
            </div>

            {/* Montant TTC */}
            <div>
              <label style={{ fontSize: "0.875rem", color: "#718096", fontWeight: "500" }}>Montant TTC</label>
              {editing ? (
                <input
                  type="number"
                  step="0.01"
                  value={editedData.totalTTC || ""}
                  onChange={(e) => handleInputChange("totalTTC", parseFloat(e.target.value))}
                  style={{ width: "100%", padding: "0.5rem", border: "1px solid #cbd5e0", borderRadius: "4px", fontSize: "1rem" }}
                />
              ) : (
                <div style={{ fontSize: "1rem", color: "#1a202c", fontWeight: "600", padding: "0.5rem 0" }}>
                  {result.totalTTC ? `${result.totalTTC} ${result.currency || 'TND'}` : "-"}
                </div>
              )}
            </div>

            {/* TVA */}
            <div>
              <label style={{ fontSize: "0.875rem", color: "#718096", fontWeight: "500" }}>TVA</label>
              {editing ? (
                <input
                  type="number"
                  step="0.1"
                  value={editedData.taxRate || ""}
                  onChange={(e) => handleInputChange("taxRate", parseFloat(e.target.value))}
                  style={{ width: "100%", padding: "0.5rem", border: "1px solid #cbd5e0", borderRadius: "4px", fontSize: "1rem" }}
                />
              ) : (
                <div style={{ fontSize: "1rem", color: "#1a202c", fontWeight: "600", padding: "0.5rem 0" }}>
                  {result.taxRate ? `${result.taxRate}%` : "-"}
                </div>
              )}
            </div>
          </div>

          {/* Indicateur de confiance */}
          {result.confidenceScore && (
            <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem", padding: "1rem", background: "#f7fafc", borderRadius: "8px" }}>
              <label style={{ fontSize: "0.875rem", color: "#718096", fontWeight: "500", minWidth: "120px" }}>
                Fiabilité de l'extraction :
              </label>
              <div style={{ flex: "1", height: "8px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
                <div 
                  style={{ 
                    height: "100%", 
                    background: "linear-gradient(90deg, #48bb78, #68d391)", 
                    width: `${result.confidenceScore}%`,
                    transition: "width 0.3s ease"
                  }} 
                ></div>
              </div>
              <span style={{ fontWeight: "600", color: "#2d3748", minWidth: "40px" }}>
                {result.confidenceScore}%
              </span>
            </div>
          )}

          {/* Boutons d'action */}
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              {!editing ? (
                <>
                  <button
                    onClick={handleEdit}
                    style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#d69e2e", textDecoration: "none", fontWeight: "500", padding: "0.5rem 1rem", border: "1px solid #faf089", borderRadius: "6px", background: "#fefcbf", transition: "all 0.2s ease", cursor: "pointer" }}
                  >
                    <span>✏️</span>
                    Modifier les données
                  </button>
                  <button
                    onClick={confirmSave}
                    disabled={saving}
                    style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "white", textDecoration: "none", fontWeight: "500", padding: "0.5rem 1rem", border: "1px solid #48bb78", borderRadius: "6px", background: saving ? "#90cdf4" : "#48bb78", transition: "all 0.2s ease", cursor: saving ? "not-allowed" : "pointer" }}
                  >
                    <span>✅</span>
                    {saving ? "Enregistrement..." : "Confirmer l'enregistrement"}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleSaveEdit}
                    style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "white", textDecoration: "none", fontWeight: "500", padding: "0.5rem 1rem", border: "1px solid #48bb78", borderRadius: "6px", background: "#48bb78", transition: "all 0.2s ease", cursor: "pointer" }}
                  >
                    <span>💾</span>
                    Sauvegarder modifications
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#718096", textDecoration: "none", fontWeight: "500", padding: "0.5rem 1rem", border: "1px solid #cbd5e0", borderRadius: "6px", background: "#f7fafc", transition: "all 0.2s ease", cursor: "pointer" }}
                  >
                    <span>❌</span>
                    Annuler
                  </button>
                </>
              )}
            </div>

            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <button
                onClick={generateEnhancedPDF}
                style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#4299e1", textDecoration: "none", fontWeight: "500", padding: "0.5rem 1rem", border: "1px solid #bee3f8", borderRadius: "6px", background: "#ebf8ff", transition: "all 0.2s ease", cursor: "pointer" }}
              >
                <span>📊</span>
                Générer PDF amélioré
              </button>
              <a
                href={`/api/invoices/${result._id}/clean/pdf`}
                target="_blank"
                rel="noreferrer"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#4299e1", textDecoration: "none", fontWeight: "500", padding: "0.5rem 1rem", border: "1px solid #bee3f8", borderRadius: "6px", background: "#ebf8ff", transition: "all 0.2s ease" }}
              >
                <span>📥</span>
                Télécharger PDF original
              </a>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}